import { ReactElement, useEffect, useMemo } from "react";
import { useRouter } from "next/router";

import { isNonEmptyArray, useBooleanState } from "@enkor/toss-slash";

import EventViewModel from "@domains/event/viewModel/EventViewModel";
import { Typo } from "@enkor/design/components";
import { grayScale } from "@enkor/design/colors";
import {
  CalendarIcon,
  KrwIcon,
  Link2Icon,
  LocationIcon,
} from "@enkor/design/icons";
import { isMobile } from "@logics/utils/isMobile";
import CategoryLabel from "@domains/common/views/components/CategoryLabel/CategoryLabel";
import { linkify } from "@logics/utils/urlHandler";
import WebViewBridge from "@logics/utils/webViewBridge";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import Avatar from "@domains/common/views/components/Avatar/Avatar";
import { useAttachLinkClickHandler } from "@logics/hooks/useAttachLinkClickHandler";

import * as S from "./EventInfoSection.style";

interface EventInfoSectionProps {
  eventDetail: EventViewModel;
}

const EventInfoSection = ({ eventDetail }: EventInfoSectionProps) => {
  const { push } = useRouter();

  const [isLongDescription, setIsLongDescription, unsetIsLongDescription] =
    useBooleanState();
  const [shouldShowAll, , , toggleDescription] = useBooleanState();

  useAttachLinkClickHandler({ id: eventDetail.id });

  const eventInfoList:
    | {
        [key in string]: {
          iconWithTitle: ReactElement;
          subTitle?: string;
          description: string | number;
        };
      }
    | null = useMemo(() => {
    if (!eventDetail) return null;

    return {
      date: {
        iconWithTitle: (
          <S.InfoIconWrapper>
            <CalendarIcon strokeColor={grayScale[800]} />
            <S.InfoIconText name="h5">Date</S.InfoIconText>
          </S.InfoIconWrapper>
        ),
        subTitle: eventDetail?.date || "",
        description: eventDetail?.time,
      },
      price: {
        iconWithTitle: (
          <S.InfoIconWrapper>
            <KrwIcon strokeColor={grayScale[800]} />
            <S.InfoIconText name="h5">Attendance</S.InfoIconText>
          </S.InfoIconWrapper>
        ),
        description: eventDetail?.attendanceFee,
      },
    };
  }, [eventDetail]);

  const moveToHostProfilePage = () => {
    if (!eventDetail?.host.id) return;

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToUserProfile(eventDetail.host.id);
    } else {
      push({
        pathname: `${NAVIGATION_ROUTE.PROFILE.HREF}/${eventDetail.host.id}`,
        query: NAVIGATION_ROUTE.PROFILE.QUERY,
      });
    }
  };

  const openMap = () => {
    if (!eventDetail) return;

    const { placeId, geo } = eventDetail.location;
    const baseUrl = `https://www.google.com/maps/search/?api=1&query=${geo.lat},${geo.lng}`;
    const url = placeId ? `${baseUrl}&query_place_id=${placeId}` : baseUrl;

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().triggerOpenMap(url);
    } else {
      window.open(url, isMobile() ? "_self" : "_blank");
    }
  };

  useEffect(() => {
    // HACK: 디자인 시스템 p 요소에 Ref를 달 수 없어 id로 요소 가져옴.
    const paragraphHeight =
      document.getElementById("event-description")?.clientHeight || 0;

    // NOTE: 5줄 이하(p 요소 height가 130 이하)인 경우 버튼 숨기기
    const MAX_PARAGRAPH_HEIGHT = 130;
    if (paragraphHeight <= MAX_PARAGRAPH_HEIGHT) {
      unsetIsLongDescription();
    } else {
      setIsLongDescription();
    }
  }, [unsetIsLongDescription, setIsLongDescription]);

  return (
    <S.Container>
      <S.DetailWrapper>
        <S.CategoryWrap>
          {isNonEmptyArray(eventDetail?.categoryList) &&
            eventDetail.categories.map((type) => (
              <CategoryLabel key={`category-${type}`} type={type} />
            ))}
        </S.CategoryWrap>
        <S.EventTitle name="h2">{eventDetail?.title}</S.EventTitle>
        <S.HostInfo type="button" onClick={moveToHostProfilePage}>
          <Avatar
            size="l"
            profile={{
              url: eventDetail?.host.profileImage || "",
              id: eventDetail?.host.id,
            }}
          />
          <S.HostName name="strongM">
            Hosted by {eventDetail?.host.name}
          </S.HostName>
        </S.HostInfo>
        <S.EventInfoSection>
          <S.EventDescription
            id="event-description"
            name="spacedM"
            dangerouslySetInnerHTML={{
              __html: linkify(eventDetail?.description, String(eventDetail.id)),
            }}
            $shouldShowAll={shouldShowAll || !isLongDescription}
          />
          {isLongDescription && (
            <S.DescriptionToggle type="button" onClick={toggleDescription}>
              Show {shouldShowAll ? "less" : "more"}
            </S.DescriptionToggle>
          )}
        </S.EventInfoSection>
      </S.DetailWrapper>

      <S.EventInfoList>
        <S.EventInfoBox>
          <S.InfoIconWrapper>
            <LocationIcon strokeColor={grayScale[800]} />
            <S.InfoIconText name="h5">Location</S.InfoIconText>
          </S.InfoIconWrapper>
          <S.LocationWrapper>
            <S.LocationDetail>
              <S.InfoName $hasLink name="strongM" role="link" onClick={openMap}>
                <span>{eventDetail?.location?.spotName}</span>
                <Link2Icon size={18} strokeColor={grayScale.white} />
              </S.InfoName>
              <Typo.Text name="xs" color={grayScale[550]}>
                {eventDetail?.location?.areaName}
              </Typo.Text>
              <Typo.Text name="s">
                {eventDetail?.location?.nearStation} nearby
              </Typo.Text>
            </S.LocationDetail>
            {eventDetail?.location?.description && (
              <div>
                <strong>Meeting point : </strong>
                <S.InfoContent
                  name="s"
                  dangerouslySetInnerHTML={{
                    __html: linkify(
                      eventDetail?.location?.description,
                      `${eventDetail.id}-location`,
                    ),
                  }}
                />
              </div>
            )}
          </S.LocationWrapper>
        </S.EventInfoBox>
        {eventInfoList &&
          Object.values(eventInfoList).map((eventInfo) => (
            <S.EventInfoBox key={eventInfo.description}>
              {eventInfo.iconWithTitle}
              <S.InfoContentWrapper>
                <S.InfoName name="strongM">{eventInfo.subTitle}</S.InfoName>
                <S.InfoContent name="s">{eventInfo.description}</S.InfoContent>
              </S.InfoContentWrapper>
            </S.EventInfoBox>
          ))}
      </S.EventInfoList>
    </S.Container>
  );
};

export default EventInfoSection;
